import type { CurrencyDefinition, LocalizedText, SourceRef, TermLanguage } from "./api/types";

export function text(value: LocalizedText | null | undefined, language: TermLanguage = "de"): string {
  if (!value) return "";
  if (language === "en") return value.en ?? value.de;
  if (language === "both" && value.en && value.en !== value.de) return `${value.de} (${value.en})`;
  return value.de;
}

export function source(ref: SourceRef | null | undefined): string {
  if (!ref) return "";
  if (ref.kind === "userDecision") return "Entscheidung";
  if (ref.kind === "import") return "Import";
  if (ref.kind === "custom") return "Eigenbau";
  if (ref.book && ref.page != null) return `${ref.book} S. ${ref.page}`;
  return ref.book ?? "";
}

const numberFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

export function number(value: number | null | undefined): string {
  return value == null ? "" : numberFormat.format(value);
}

export function signed(value: number): string {
  return value > 0 ? `+${number(value)}` : number(value);
}

const dateFormat = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

export function dateTime(value: string): string {
  return dateFormat.format(new Date(value));
}

/**
 * Formats an amount of a currency in its display units, largest first, e.g. "3 L 50 T" or "35 EP".
 * Amounts are stored in the smallest unit; without units the plain number is shown. With sign, positive amounts get a "+".
 */
export function formatAmount(currency: CurrencyDefinition | undefined, amount: number, withSign = false): string {
  const units = [...(currency?.units ?? [])].sort((a, b) => b.factor - a.factor);
  if (units.length === 0) return withSign ? signed(amount) : number(amount);
  const smallest = units[units.length - 1]!;
  let rest = Math.abs(currency?.decimals ? amount : Math.round(amount));
  const parts: string[] = [];
  for (const unit of units) {
    const count = unit === smallest ? rest / unit.factor : Math.floor(rest / unit.factor);
    if (count > 0 || (unit === smallest && parts.length === 0)) {
      parts.push(`${number(count)} ${unit.symbol}`);
      rest -= count * unit.factor;
    }
  }
  return `${amount < 0 ? "−" : withSign && amount > 0 ? "+" : ""}${parts.join(" ")}`;
}
